/**
 * Privacy / consent state, GTM Consent Mode v2 sync, and recording.
 *
 * Surface model:
 *   - EEA/UK/Swiss visitors see the popup banner on first visit. Everyone
 *     else (US-first audience) follows the no-banner model — privacy
 *     controls live on the /privacy-settings page linked from the footer.
 *   - This module is the central state API. Read via readConsent() /
 *     getCurrentPrefs(); write via saveConsent() from the settings page or
 *     the banner.
 *   - gh_consent localStorage shape is unchanged so the inline rehydrate
 *     script in index.html keeps working for returning users.
 *
 * Sec-GPC (Global Privacy Control) is honored at boot via initConsent() —
 * see the function comment for details. CPRA §1798.135 and Colorado's UOOM
 * rules treat Sec-GPC as a binding opt-out signal.
 *
 * Pattern ported from Red Nets (sf_rednets25_web/sfcweb/src/pages/Home/components/Footer/Footer.jsx
 * + sfcweb/src/utils/api.js logConsent).
 */
import { isGdprCountry } from './gdprCountries';
import { readCachedViewerCountry } from './geo';
import { readCloudCacheAnalytics, readCloudCacheMarketing } from './cloudcache';
export type ConsentPrefs = {
  /**
   * Strictly necessary cookies/storage. Always true; surfaced in the UI as a
   * disabled "always on" row so the user can SEE what runs unconditionally
   * (session cookie, refresh-token, this consent record itself, country cache,
   * the consent rehydrate script in index.html). Required for GDPR Art. 5(1)(a)
   * transparency — a 2-tier model that hides "necessary" reads as opaque to
   * auditors.
   */
  necessary: true;
  /** Plausible (cookieless) + GTM analytics_storage. */
  analytics: boolean;
  /** DEEL + GTM ad_storage / ad_user_data / ad_personalization. */
  marketing: boolean;
};

export type ConsentRecord = {
  prefs: ConsentPrefs;
  version: string;
  savedAt: string;
};

/**
 * Bump when the tier model or the prefs shape changes materially.
 * '2026-v1' was the 2-tier model {analytics, marketing}; '2026-v2' adds the
 * explicit `necessary: true` row. readConsent() accepts both and coerces v1
 * records to the v2 shape in memory; the next saveConsent() call re-stamps.
 */
export const CONSENT_VERSION = '2026-v2';

// IMPORTANT: this key is duplicated as a string literal in web/index.html's
// inline rehydrate script (the one that fires gtag('consent','update',...)
// before GTM loads for returning users). If you rename STORAGE_KEY here,
// update that script too - otherwise returning users will silently lose
// their prior consent state on every page load.
const STORAGE_KEY = 'gh_consent';
const SESSION_KEY = 'gh_consent_sid';
const QUEUE_KEY = 'gh_consent_log_queue';

const DEFAULT_PREFS: ConsentPrefs = { necessary: true, analytics: false, marketing: false };

/**
 * The pre-interaction default for a viewer, by region. This is the TypeScript
 * mirror of the geo-aware Consent Mode defaults in index.html — keep the two
 * in step:
 *   - GDPR/UK/CH (opt-in): all non-essential denied until the banner Accept.
 *   - Everyone else (US-first opt-out regime): granted by default.
 *
 * `country` is the value from useViewerCountry().country. null/unknown is
 * treated as non-GDPR (granted), matching Google's edge behavior for
 * unrecognized regions — the authoritative gate is the region-scoped default
 * in index.html, which Google enforces from the viewer IP; this helper only
 * drives what the /privacy-settings toggles SHOW before the user has saved a
 * choice.
 *
 * GPC is handled out of band: initConsent() writes a deny-all record when GPC
 * is present, so a GPC viewer always has a stored record and this default is
 * not consulted for them.
 */
export function getDefaultPrefs(country: string | null | undefined): ConsentPrefs {
  if (isGdprCountry(country)) {
    return { necessary: true, analytics: false, marketing: false };
  }
  return { necessary: true, analytics: true, marketing: true };
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readConsent(): ConsentRecord | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> & {
      prefs?: Partial<ConsentPrefs>;
    };
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.prefs ||
      typeof parsed.prefs.analytics !== 'boolean' ||
      typeof parsed.prefs.marketing !== 'boolean' ||
      typeof parsed.version !== 'string' ||
      typeof parsed.savedAt !== 'string'
    ) {
      return null;
    }
    // Backward-compat: '2026-v1' records lack the `necessary` field. Coerce in
    // memory; the next saveConsent() will re-stamp under the current version.
    return {
      prefs: {
        necessary: true,
        analytics: parsed.prefs.analytics,
        marketing: parsed.prefs.marketing,
      },
      version: parsed.version,
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
}

export function getCurrentPrefs(): ConsentPrefs {
  return readConsent()?.prefs ?? { ...DEFAULT_PREFS };
}

export function getOrCreateSessionId(): string {
  const ss = safeSessionStorage();
  if (!ss) return crypto.randomUUID();
  let id = ss.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    try {
      ss.setItem(SESSION_KEY, id);
    } catch {
      /* sessionStorage write blocked */
    }
  }
  return id;
}

export function applyGtmConsent(prefs: ConsentPrefs): void {
  try {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
    });
  } catch {
    /* gtag blocked by tracking protection */
  }
}

/**
 * Targeted gtag update — touches analytics_storage only, leaves the ad
 * storage keys alone. Used by runSfCookieSync to apply the State Farm DNS
 * Analytics opt-out signal without disturbing our own marketing-toggle
 * state.
 */
function applyAnalyticsOnly(granted: boolean): void {
  try {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  } catch {
    /* gtag blocked by tracking protection */
  }
}

/**
 * True if the browser is asserting Sec-GPC. GPC (Global Privacy Control) is
 * a binding opt-out signal under CPRA §7025 and Colorado's UOOM rules; when
 * present, we deny everything regardless of stored gh_consent, SF cookie
 * state, or region defaults.
 */
export function isGpcActive(): boolean {
  try {
    return (
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
    );
  } catch {
    return false;
  }
}

const DENIED_PREFS: ConsentPrefs = { necessary: true, analytics: false, marketing: false };

/**
 * Compute the effective consent prefs after applying the full precedence
 * chain. Highest priority first:
 *
 *   1. GPC active                 → deny everything.
 *   2. SF _Analytics cookie (US)  → beats stored analytics tier. '0' denies,
 *                                    '1' grants. Absent → no signal.
 *   3. Stored gh_consent record   → user's explicit on-site choice.
 *   4. Region defaults            → US-first: granted; GDPR/UK/CH: denied.
 *
 * Runtime callers (initConsent, syncDeel/syncPlausible, PrivacySettingsPage)
 * should read from here rather than raw readConsent() so the GPC and SF
 * signals are honored uniformly.
 */
export function computeEffectivePrefs(country: string | null): ConsentPrefs {
  if (isGpcActive()) return DENIED_PREFS;
  const stored = readConsent();
  const base = stored?.prefs ?? getDefaultPrefs(country);
  if (country === 'US') {
    const sf = readCloudCacheAnalytics();
    if (sf !== 'unknown') {
      return { ...base, analytics: sf === 'granted' };
    }
  }
  return base;
}

/**
 * Runtime sync from the State Farm "Do Not Sell or Share" preference center
 * into our GTM Consent Mode v2 state AND Plausible loader. US-only,
 * Analytics-only.
 *
 * Precedence (highest priority first — this function bails on GPC, which
 * has already denied everything via initConsent, and on 'unknown' cookie):
 *
 *   1. GPC                — handled at initConsent; if GPC is active, GTM
 *                            is already denied and this function is a no-op.
 *   2. SF Analytics cookie — this function. Acts on both '0' (deny) and
 *                            '1' (grant, applied on top of stored or
 *                            default state).
 *   3. Stored gh_consent  — the analytics tier is overridden by the SF
 *                            cookie when present (per client direction:
 *                            "tie the analytics switch to the SF cookie").
 *
 * Deliberately does NOT write a gh_consent record — the SF cookie is an
 * external signal, not an explicit consent given on this site. Absence of
 * a record means a later flip on the SF DNS page picks up on the next
 * pageload without needing to invalidate a stale local copy.
 *
 * Idempotent: safe to call from both the synchronous boot path (returning
 * users with cached country) and from a React effect after geo resolves
 * (first-time visitors).
 */
export function runSfCookieSync(country: string | null): void {
  if (country !== 'US') return;
  if (isGpcActive()) return;
  const state = readCloudCacheAnalytics();
  if (state === 'unknown') return;
  const analyticsGranted = state === 'granted';
  applyAnalyticsOnly(analyticsGranted);
  // Also gate Plausible on the SF signal: deny → unload; grant → attempt
  // load (the loader re-runs all gates so a still-denying gh_consent or
  // plausible_ignore keeps it off).
  if (analyticsGranted) {
    try { window.__loadPlausible?.(); } catch { /* stripped in dev */ }
  } else {
    try { window.__unloadPlausible?.(); } catch { /* stripped in dev */ }
  }
}

/**
 * Live load/unload of Plausible based on the analytics pref. Mirrors
 * syncDeel: the __loadPlausible / __unloadPlausible globals are defined
 * by an inline IIFE inside the PLAUSIBLE:start..PLAUSIBLE:end block in
 * web/index.html. __loadPlausible re-runs its own gate chain (GPC,
 * plausible_ignore, gh_consent.analytics=false, SF _Analytics=0) so a
 * mid-session load call can't override a still-active deny signal.
 *
 * When VITE_PLAUSIBLE_DOMAIN is empty the block is stripped at build
 * time and the optional-chain calls below are a clean no-op.
 */
export function syncPlausible(prefs: ConsentPrefs): void {
  try {
    if (prefs.analytics && !isGpcActive()) {
      window.__loadPlausible?.();
    } else {
      window.__unloadPlausible?.();
    }
  } catch {
    /* Plausible globals undefined (stripped) or DOM access blocked */
  }
}

/**
 * Whether a tracker's <script> element is currently in the document. The id
 * / selector are duplicated from the loader IIFEs in web/index.html
 * (DEEL:start / PLAUSIBLE:start / SFGTM:start blocks) — keep them in sync.
 *
 * Used by the interactive consent surfaces (banner Reject, settings-page
 * tier toggles) to decide whether a revocation needs a page reload:
 * __unloadDeel / __unloadPlausible only remove the script ELEMENT — code the
 * tracker already executed (Adobe's beacon machinery, Plausible's history
 * hook) keeps running until the page unloads, so removal alone does not
 * stop an in-session tracker. GTM/GA never needs this: gtag re-checks
 * consent state on every hit, so a consent update takes effect immediately
 * without a reload.
 */
export function isDeelScriptPresent(): boolean {
  try {
    return document.getElementById('gh-deel-script') !== null;
  } catch {
    return false;
  }
}

export function isPlausibleScriptPresent(): boolean {
  try {
    return document.querySelector('script[src^="/p/js/"]') !== null;
  } catch {
    return false;
  }
}

export function isSfGtmPresent(): boolean {
  try {
    return document.getElementById('gh-sf-gtm-script') !== null;
  } catch {
    return false;
  }
}

export type ConsentAction = 'initial' | 'updated';

export type ConsentLogPayload = {
  sessionId: string;
  prefs: ConsentPrefs;
  action: ConsentAction;
  consentVer: string;
  source: string;
  client_ts: string;
  pageUrl: string;
  userAgent: string;
};

/**
 * POST one consent payload. Returns true if the server accepted it (2xx),
 * false otherwise (including 5xx, network error, no API URL configured).
 * Retries once on 5xx — the kind of error that's most often transient
 * (cold DB pool, restart) rather than a persistent rejection.
 */
async function postConsentLog(payload: ConsentLogPayload): Promise<boolean> {
  const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
  if (!apiBase) return false;
  const url = `${apiBase.replace(/\/$/, '')}/privacy/consent`;
  const body = JSON.stringify(payload);
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'include',
    keepalive: true,
  };
  try {
    const res = await fetch(url, init);
    if (res.ok) return true;
    if (res.status >= 400 && res.status < 500) return false; // bad payload, no retry
    // 5xx — retry once.
    const retry = await fetch(url, init);
    return retry.ok;
  } catch {
    return false;
  }
}

function readQueue(): ConsentLogPayload[] {
  const ss = safeSessionStorage();
  if (!ss) return [];
  try {
    const raw = ss.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ConsentLogPayload[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(q: ConsentLogPayload[]): void {
  const ss = safeSessionStorage();
  if (!ss) return;
  try {
    if (q.length === 0) ss.removeItem(QUEUE_KEY);
    else ss.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* sessionStorage write blocked */
  }
}

/**
 * Best-effort consent log to the API. Tries once with one retry on 5xx via
 * postConsentLog(); if the server is unreachable or still failing, queues
 * the payload in sessionStorage for replay on the next initConsent() call.
 * The queue is capped at 10 entries to bound storage growth — the audit
 * value of older queued events is low (the user has already moved on).
 */
export async function logConsent(
  prefs: ConsentPrefs,
  action: ConsentAction,
  source: string,
): Promise<void> {
  const payload: ConsentLogPayload = {
    sessionId: getOrCreateSessionId(),
    prefs,
    action,
    consentVer: CONSENT_VERSION,
    source,
    client_ts: new Date().toISOString(),
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
  };
  const ok = await postConsentLog(payload);
  if (!ok) {
    const q = readQueue();
    q.push(payload);
    writeQueue(q.slice(-10));
  }
}

export function saveConsent(
  prefs: ConsentPrefs,
  action: ConsentAction,
  source: string,
): void {
  const record: ConsentRecord = {
    prefs,
    version: CONSENT_VERSION,
    savedAt: new Date().toISOString(),
  };
  const ls = safeLocalStorage();
  if (ls) {
    try {
      ls.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* localStorage quota or blocked */
    }
  }
  applyGtmConsent(prefs);
  syncDeel(prefs);
  syncSfGtm(prefs);
  syncPlausible(prefs);
  // Fire-and-forget; never blocks UI.
  void logConsent(prefs, action, source).catch(() => {});
}

/**
 * Load-only sync for the State Farm enterprise GTM container (SFGTM block
 * in index.html, GTM-TLWQWQT). Same gate outcomes as syncDeel (US-only +
 * marketing tier + GPC via the loader) with one addition: the SF _Marketing
 * cookie — SF's "applicable privacy indicator" for their retargeting
 * container — blocks on '0'.
 *
 * There is deliberately no unload arm: GTM cannot be un-executed once
 * loaded. Revocation is handled by the interactive consent surfaces, which
 * force a page reload when the container script is present (see
 * isSfGtmPresent). __loadSfGtm re-runs the full gate chain internally and
 * fails CLOSED on any read error, so a stale call here cannot load the
 * container over a fresh opt-out.
 */
export function syncSfGtm(prefs: ConsentPrefs): void {
  try {
    if (!prefs.marketing) return;
    if (readCachedViewerCountry() !== 'US') return;
    if (readCloudCacheMarketing() === 'denied') return;
    window.__loadSfGtm?.();
  } catch {
    /* SFGTM global undefined (stripped) or DOM access blocked */
  }
}

/**
 * Live load/unload of the DEEL tracking script based on the marketing pref
 * AND a hard geo gate to US-only viewers.
 *
 * Per client direction (2026-06): DEEL must not load for any visitor outside
 * the United States, regardless of consent state. This function enforces
 * that — even if `prefs.marketing === true`, a non-US (or unknown) country
 * falls through to the unload path so any earlier-loaded script element is
 * removed. The marketing toggle still controls GTM's ad_storage family
 * (which is region-agnostic Consent Mode v2 state), so the toggle remains
 * meaningful outside the US even though DEEL itself does not participate.
 *
 * window.__loadDeel / __unloadDeel are defined by an inline IIFE in
 * web/index.html inside the DEEL:start..DEEL:end block, so they share the
 * SAME prod-host literal that the Vite stateFarmEndpointsPlugin rewrites at
 * build time — no duplicated host string in TS. When VITE_DEEL_ENABLED is
 * not 'true' the entire block (including these globals) is stripped, so the
 * optional-chain calls below are a clean no-op in dev builds.
 */
export function syncDeel(prefs: ConsentPrefs): void {
  try {
    const country = readCachedViewerCountry();
    if (prefs.marketing && country === 'US') {
      window.__loadDeel?.();
    } else {
      window.__unloadDeel?.();
    }
  } catch {
    /* DEEL globals undefined (stripped) or DOM access blocked */
  }
}

/**
 * One-time boot wiring for the consent system. Called once from main.tsx
 * BEFORE React mounts so that:
 *
 *   1. Sec-GPC honoring. If navigator.globalPrivacyControl === true, we
 *      apply a runtime deny-all state (GTM + DEEL + Plausible) regardless
 *      of whether a gh_consent record exists. GPC is a binding opt-out
 *      signal (CPRA §7025, CO UOOM) and wins over any prior in-band
 *      choice for the duration of the signal. If no gh_consent record
 *      exists yet we ALSO write one (source: 'gpc-signal') so the
 *      ConsentBanner stays hidden and the audit log captures the event.
 *      If a record already exists we leave it in place — the user's prior
 *      choice is preserved for the case where GPC is later disabled.
 *
 *   2. State Farm DNS Analytics-cookie sync (US only). If the viewer is in
 *      the US (per cached geo) and GPC is not active, apply the SF
 *      _Analytics cookie to GTM + Plausible at runtime. Runs synchronously
 *      here for returning visitors with cached country; the SfCookieSync
 *      component re-runs it after geo resolves for first-time visitors.
 *
 *   3. Queue replay. If a previous pageload queued any failed consent
 *      logs in sessionStorage, we drain that queue now while the API is
 *      likely warm. Anything still failing stays queued for next time.
 *
 * Idempotent — safe to call more than once.
 */
export function initConsent(): void {
  // GPC: always applies runtime deny; conditionally writes a record.
  try {
    if (isGpcActive()) {
      if (readConsent() === null) {
        // First encounter with GPC. Write a deny-all record + apply state
        // + audit-log. saveConsent handles GTM, DEEL, Plausible, and the
        // best-effort server log — same machinery used by the banner.
        saveConsent(DENIED_PREFS, 'initial', 'gpc-signal');
      } else {
        // Existing gh_consent record. Do NOT overwrite (preserves user's
        // prior on-site choice for when GPC is later disabled). Instead,
        // force the runtime state to denied — the pre-GTM inline block
        // has already denied GTM synchronously; this catches the DEEL and
        // Plausible loaders too in case a returning-visitor auto-load
        // path fired before initConsent ran.
        applyGtmConsent(DENIED_PREFS);
        syncDeel(DENIED_PREFS);
        syncPlausible(DENIED_PREFS);
      }
    }
  } catch {
    /* navigator access blocked or weird environment */
  }

  // State Farm DNS Analytics-cookie sync — only fires for US returning
  // visitors whose country is cached. First-time visitors hit this path
  // via SfCookieSync.tsx once useViewerCountry resolves. runSfCookieSync
  // is a no-op when GPC is active (already denied everything above).
  try {
    runSfCookieSync(readCachedViewerCountry());
  } catch {
    /* belt-and-suspenders; the function itself is already defensive */
  }

  // Replay any queued consent logs from a previous failed POST. Fire-and-forget;
  // we don't want to delay the React mount on this.
  void replayQueuedConsentLogs();
}

async function replayQueuedConsentLogs(): Promise<void> {
  const queue = readQueue();
  if (queue.length === 0) return;
  const remaining: ConsentLogPayload[] = [];
  for (const payload of queue) {
    const ok = await postConsentLog(payload);
    if (!ok) remaining.push(payload);
  }
  writeQueue(remaining);
}
